/**
 * QuickActionsMenu Component
 *
 * Context menu for document quick actions: Rename, Delete, Duplicate, Move.
 *
 * @module features/dashboard/components/QuickActionsMenu
 * @see SPEC-016 Requirements 7.2, 7.3, 7.5, 7.6
 */

import React, { useEffect, useRef, useCallback } from 'react';
import { cn } from '../../../lib/cn';
import type { DocListItemInput } from '@linkode/shared';

// ============================================================================
// Types
// ============================================================================

/**
 * Quick action types
 */
export type QuickAction = 'rename' | 'delete' | 'duplicate' | 'move';

/**
 * Menu position
 */
export interface MenuPosition {
  x: number;
  y: number;
}

export interface QuickActionsMenuProps {
  /** Document to perform actions on */
  doc: DocListItemInput;
  /** Menu position (from right-click event) */
  position: MenuPosition;
  /** Whether menu is visible */
  isOpen: boolean;
  /** Close menu handler */
  onClose: () => void;
  /** Action handler */
  onAction: (action: QuickAction, doc: DocListItemInput) => void;
  /** Additional class names */
  className?: string;
}

// ============================================================================
// Action Definitions
// ============================================================================

interface ActionDefinition {
  id: QuickAction;
  label: string;
  icon: React.ReactNode;
  shortcut?: string;
  danger?: boolean;
}

const actions: ActionDefinition[] = [
  {
    id: 'rename',
    label: 'Rename',
    shortcut: 'F2',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
        />
      </svg>
    ),
  },
  {
    id: 'duplicate',
    label: 'Duplicate',
    shortcut: 'Ctrl+D',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z"
        />
      </svg>
    ),
  },
  {
    id: 'move',
    label: 'Move to...',
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z"
        />
      </svg>
    ),
  },
  {
    id: 'delete',
    label: 'Delete',
    shortcut: 'Del',
    danger: true,
    icon: (
      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
        />
      </svg>
    ),
  },
];

// ============================================================================
// Component
// ============================================================================

/**
 * QuickActionsMenu Component
 *
 * Features:
 * - Context menu positioning
 * - Keyboard navigation
 * - Click outside to close
 * - Escape to close
 * - Action shortcuts display
 *
 * @example
 * ```tsx
 * <QuickActionsMenu
 *   doc={selectedDoc}
 *   position={{ x: event.clientX, y: event.clientY }}
 *   isOpen={menuOpen}
 *   onClose={() => setMenuOpen(false)}
 *   onAction={(action, doc) => handleAction(action, doc)}
 * />
 * ```
 *
 * @see Requirements 7.2, 7.3, 7.5, 7.6
 */
export const QuickActionsMenu: React.FC<QuickActionsMenuProps> = ({
  doc,
  position,
  isOpen,
  onClose,
  onAction,
  className,
}) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const [focusedIndex, setFocusedIndex] = React.useState(0);

  // Handle click outside
  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        onClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen, onClose]);

  // Handle keyboard navigation
  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      switch (event.key) {
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
        case 'ArrowDown':
          event.preventDefault();
          setFocusedIndex((prev) => (prev + 1) % actions.length);
          break;
        case 'ArrowUp':
          event.preventDefault();
          setFocusedIndex((prev) => (prev - 1 + actions.length) % actions.length);
          break;
        case 'Enter':
        case ' ': {
          event.preventDefault();
          const action = actions[focusedIndex];
          if (action) {
            onAction(action.id, doc);
            onClose();
          }
          break;
        }
      }
    },
    [doc, focusedIndex, onAction, onClose]
  );

  // Focus menu when opened
  useEffect(() => {
    if (isOpen && menuRef.current) {
      menuRef.current.focus();
      setFocusedIndex(0);
    }
  }, [isOpen]);

  // Adjust position to stay within viewport
  const adjustedPosition = React.useMemo(() => {
    if (typeof window === 'undefined') return position;

    const menuWidth = 200;
    const menuHeight = actions.length * 40 + 16; // Approximate height

    let x = position.x;
    let y = position.y;

    // Adjust horizontal position
    if (x + menuWidth > window.innerWidth) {
      x = window.innerWidth - menuWidth - 8;
    }

    // Adjust vertical position
    if (y + menuHeight > window.innerHeight) {
      y = window.innerHeight - menuHeight - 8;
    }

    return { x: Math.max(8, x), y: Math.max(8, y) };
  }, [position]);

  if (!isOpen) return null;

  return (
    <div
      ref={menuRef}
      className={cn(
        'fixed z-50',
        'min-w-[200px]',
        'bg-white',
        'border-2 border-linkode-text',
        'rounded-linkode',
        'shadow-linkode-card',
        'py-2',
        'outline-none',
        className
      )}
      style={{
        left: adjustedPosition.x,
        top: adjustedPosition.y,
      }}
      role="menu"
      aria-label="Document actions"
      tabIndex={-1}
      onKeyDown={handleKeyDown}
    >
      {actions.map((action, index) => (
        <button
          key={action.id}
          className={cn(
            'w-full px-4 py-2',
            'flex items-center gap-3',
            'text-left font-mono text-sm',
            'transition-colors duration-150',
            action.danger
              ? 'text-linkode-red hover:bg-linkode-red/10'
              : 'text-linkode-text hover:bg-linkode-bg-light',
            focusedIndex === index && (action.danger ? 'bg-linkode-red/10' : 'bg-linkode-bg-light')
          )}
          role="menuitem"
          onClick={() => {
            onAction(action.id, doc);
            onClose();
          }}
          onMouseEnter={() => setFocusedIndex(index)}
        >
          {action.icon}
          <span className="flex-1">{action.label}</span>
          {action.shortcut && (
            <span className="text-xs text-linkode-text/50">{action.shortcut}</span>
          )}
        </button>
      ))}
    </div>
  );
};

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Generate duplicate title with "(Copy)" suffix
 *
 * @param originalTitle - Original document title
 * @returns New title with "(Copy)" suffix
 * @see Requirement 7.5
 */
export function generateDuplicateTitle(originalTitle: string): string {
  // Check if title already ends with "(Copy)" or "(Copy N)"
  // Use a pattern that captures the base title separately to preserve whitespace
  const copyPattern = /^(.*?) \(Copy(?:\s+(\d+))?\)$/;
  const match = originalTitle.match(copyPattern);

  if (match) {
    // Already has copy suffix, increment number
    const baseTitle = match[1];
    const currentNum = match[2] ? parseInt(match[2], 10) : 1;
    return `${baseTitle} (Copy ${currentNum + 1})`;
  }

  // Add "(Copy)" suffix
  return `${originalTitle} (Copy)`;
}
